import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { identityApiRef, useApi, configApiRef } from '@backstage/core-plugin-api';
import { AiModalProps, Message } from './types';
import { ModalHeader } from './ModalHeader';
import { ExampleQuestions } from './ExampleQuestions';
import { ChatHistory } from './ChatHistory';
import { ChatInput } from './ChatInput';
import { generateSessionId, queryAgent } from './aiService';

const exampleQuestions = [
  'How do I onboard my product to marketplace?',
  'What are the different user roles and permissions?',
  'How does STS infrastructure provisioning work?',
  'How can I track usage metrics for my product?',
];

export const AiModal: React.FC<AiModalProps> = ({ open, onClose }) => {
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => generateSessionId());
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const handleClearChat = () => {
    setMessages([]);
    setQuestion('');
  };

  const handleQuestionClick = (q: string) => {
    setQuestion(q);
    setTimeout(() => {
      const form = document.querySelector('form');
      if (form) {
        form.requestSubmit();
      }
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim()) return;

    const userMessage: Message = { role: 'user', content: question };
    setMessages(prev => [...prev, userMessage]);
    const currentQuestion = question;
    setQuestion('');
    setIsLoading(true);

    const { response, sources } = await queryAgent(currentQuestion, sessionId, identityApi, configApi);
    const aiResponse: Message = {
      role: 'assistant',
      content: response,
      sources,
    };
    setMessages(prev => [...prev, aiResponse]);
    setIsLoading(false);
  };

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1300,
        }}
        onClick={onClose}
      />
      <Box
        sx={{
          position: 'fixed',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          maxWidth: '672px',
          bgcolor: 'white',
          borderRadius: '8px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          zIndex: 1301,
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ModalHeader
          onClose={onClose}
          onClearChat={handleClearChat}
          showClear={messages.length > 0}
        />
        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {messages.length === 0 ? (
              <ExampleQuestions
                questions={exampleQuestions}
                onQuestionClick={handleQuestionClick}
              />
            ) : (
              <ChatHistory messages={messages} isLoading={isLoading} />
            )}
            <div ref={chatEndRef} />
          </Box>
        </Box>
        <ChatInput
          value={question}
          onChange={setQuestion}
          onSubmit={handleSubmit}
        />
      </Box>
    </>
  );
};
